import assert from 'assert';
import EventEmitter from 'events';
import { namespaces, translate } from 'us-react-components/dist/hooks/useSharedTranslation';
import { action, makeObservable, observable, runInAction } from 'mobx';

import { Notification, NotificationService } from 'us-web-services';
import { Storage } from '../../constants';
import { UserStore } from '../UserStore';

export class NotificationStore extends EventEmitter {
  #userId: number;
  unreadNotifications?: Array<Notification> = [];
  unreadNotificationsCount: number = 0;

  constructor(user: UserStore) {
    super();
    this.#userId = user.user.id!;

    makeObservable(this, {
      unreadNotifications: observable,
      unreadNotificationsCount: observable,
      refreshNotifications: action,
    });

    this.refreshNotifications();
    this.addListener('timeout', this.refreshNotifications);
    setInterval(() => this.emit('timeout'), 60000);
  }

  static #getNps = (): Notification | null => {
    const promptId = localStorage.getItem(Storage.NPS_PROMPT_ID);

    if (!promptId) {
      return null;
    }

    const npsNotification: Notification = {
      label: translate('notifications.nps.label', namespaces.caregiver),
      message: translate('notifications.nps.message', namespaces.caregiver),
      sender: {
        pictureUrl: 'https://assets.urbansitter.com/static/img/logos/us-avatar.webp',
      },
      promptId: +promptId,
      read: true,
    };

    return npsNotification;
  };

  markRead = async (id: number): Promise<void> => {
    const notification = {
      receiverId: id,
      userId: id,
    };
    try {
      await NotificationService.patch(notification);
    } catch (error) {
      console.warn('[NotificationStore] markRead failed', error);
      return;
    }

    runInAction(() => {
      localStorage.removeItem(Storage.NPS_PROMPT_ID);
      this.unreadNotificationsCount = 0;
      this.unreadNotifications = [];
    });
    void this.refreshNotifications();
  };

  refreshNotifications = async (): Promise<void> => {
    const config = {
      params: {
        'created.dir': 'desc',
        limit: 4,
        read: false,
        receiverId: this.#userId,
        sort: 'created',
      },
    };
    const response = (await NotificationService.getByFilter(config)).data;

    const nps = NotificationStore.#getNps();
    if (nps) {
      response.data.push(nps);
    }

    // Updating the notifications causes a re-render, even if the data is the same
    try {
      assert.deepEqual(this.unreadNotifications, response.data);
    } catch {
      runInAction(() => {
        this.unreadNotifications = response.data;
        this.unreadNotificationsCount = response.meta.size;
        if (nps) {
          this.unreadNotificationsCount += 1;
        }
      });
    }
  };
}
